import classNames from 'classnames';
import { DisplayForecastFreezingLevel } from 'common/display-state/display-forecast-freezing-level.js';
import { DisplayForecastWind } from 'common/display-state/display-forecast-wind.js';
import { DisplayTime } from 'common/display-state/display-time.js';
import { IconWithText } from 'common/icon/icon-with-text.js';
import { IconNameV1 } from 'common/icon/iconV1.js';
import { INote, RenderNotes } from 'common/render-notes.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import * as React from 'react';
import { IModule } from 'redux-state/layout/types.js';

interface IForecastProps {
  content: IForecast[];
  notes?: INote[];
}

interface IForecast {
  title?: string;
  subTitle?: string;
  icon?: IconNameV1;
  text?: string;
  issuedAt?: string; // ISO 8601
  wind1000?: string;
  wind2000?: string;
  freezingLevel?: string;
}

function ForecastIcon({ icon, children }: { icon?: IconNameV1; children: React.ReactNode }) {
  if (icon) {
    return (
      <IconWithText icon={`condition-${icon}` as IconNameV1} classes="IconWithText--responsive">
        {children}
      </IconWithText>
    );
  }

  return <>{children}</>;
}

const Forecast = (props: IModule<IForecastProps>) => {
  const { content, notes } = props.config;

  if (!content) {
    return null;
  }

  return (
    <Tile dataModule="forecast-mod" {...props}>
      <div className="Tile-body">
        <TileBodyHeaderV2
          isDrawer={props.isDrawer}
          modulePath={props.modulePath}
          config={props.config}
          classes={'u-mB-sm'}
        />
        {content.map((val: IForecast, idx: number) => {
          const { icon, title, text, issuedAt, subTitle, freezingLevel, wind1000, wind2000 } = val;

          return (
            <ForecastIcon key={idx} icon={icon}>
              <>
                {title && (
                  <div className={classNames('u-flex', { 'u-mB-xs': !icon })}>
                    <h3 className="u-textBold h2 u-mT-0 u-mB-0">{title}</h3>

                    {subTitle && <h4 className="u-textRegular u-mT-0 u-mB-0 h2">&nbsp;{subTitle}</h4>}
                  </div>
                )}

                {text && <p className="u-mT-0 u-mB-xs">{text}</p>}

                {(wind1000 || wind2000 || freezingLevel) && (
                  <div className="u-mT-sm u-mB-sm">
                    {wind1000 && <DisplayForecastWind wind={wind1000} type="1000" />}

                    {wind2000 && <DisplayForecastWind wind={wind2000} type="2000" />}

                    {freezingLevel && <DisplayForecastFreezingLevel freezingLevel={freezingLevel} />}
                  </div>
                )}

                {issuedAt && (
                  <small className="fineprint u-block u-textGrey u-mT-xs u-mB-sm">
                    {issuedAt && (
                      <>
                        Issued: <DisplayTime date={issuedAt} />
                      </>
                    )}
                  </small>
                )}
              </>
            </ForecastIcon>
          );
        })}

        {notes && (
          <>
            <hr className="u-md-mT-sm u-mB-sm" />
            <RenderNotes notes={notes} />
          </>
        )}
      </div>
    </Tile>
  );
};
export { Forecast };
